import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { GeckoWeld } from 'types/assetModel/features/welds/geckoWelds'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

export interface GeckoVerticalWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.VerticalWeld
  feature_geometry: {
    y_start: number
    y_end: number
    x: number
  }
}

export const NewGeckoVerticalWeldJSON: GeckoVerticalWeldJSON = {
  name: 'New Vertical Weld',
  type: GeckoFeatureModelType.VerticalWeld,
  feature_geometry: {
    y_start: 0,
    y_end: 0,
    x: 0,
  },
}

export class GeckoVerticalWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.VerticalWeld

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoVerticalWeldJSON
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x as number,
      feature_geometry.y_start as number,
      feature_geometry.x as number,
      feature_geometry.y_end as number
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJson: Record<string, GeckoVerticalWeldJSON>
  ): Map<string, GeckoVerticalWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoVerticalWeld(componentID, weldID, recordsJson[weldID])
      )
      return resultObj
    }, new Map<string, GeckoVerticalWeld>())
  }

  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoVerticalWeldJSON
  ): GeckoVerticalWeld {
    return new GeckoVerticalWeld(componentID, weldID, weldJson)
  }

  public asJSON() {
    return generateVerticalWeld(
      this.name,
      this.feature_geometry.x.min,
      this.feature_geometry.y.min,
      this.feature_geometry.y.max
    )
  }
}

export function generateVerticalWeld(
  name: string | undefined,
  x: GeckoMeasurement,
  yStart: GeckoMeasurement,
  yEnd: GeckoMeasurement
): GeckoVerticalWeldJSON {
  const verticalWeldJSON: GeckoVerticalWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.VerticalWeld,
    feature_geometry: {
      x: x
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      y_start: yStart
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      y_end: yEnd
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
  }

  return verticalWeldJSON
}

export const VerticalWeldModelFormFields = (
  weldJSON: GeckoVerticalWeldJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: weldJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: weldJSON.feature_geometry.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y_end',
      label: 'Y End',
      value: weldJSON.feature_geometry.y_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const VerticalWeldJSONFromFormFields = (formFields: FormField[]) => {
  return generateVerticalWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_end')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const VerticalWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.VerticalWeld,
  title: 'Vertical Welds',
  singularName: 'Vertical Weld',
  fields: ['x', 'y_start', 'y_end'],
  sortBy: [
    { fieldName: 'y_start', order: 'asc' },
    { fieldName: 'x', order: 'asc' },
  ],
  disableEditing: true,
  tableMessage:
    'Adding / Editing of Vertical Welds is disabled as they are automatically generated from Plates and/or Courses',
}
